'use client'

import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button, Divider, Drawer, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { FilterState, useFilterState } from '../hooks/use-filter-state'
import { CheckboxFilter, ColorFilter, PriceFilter, SizeFilter } from './filter-sections'
import { determineFilterType } from './filter-sections/filter-config'

interface FilterSidebarProps {
  open: boolean
  isLoading?: boolean
  onClose: () => void
  aggregations: IAggregations[]
  onLoadingChange?: (loading: boolean) => void
  onFilterApplied?: () => void
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  open,
  onClose,
  isLoading,
  aggregations,
  onLoadingChange,
  onFilterApplied,
}) => {
  // Sử dụng custom hook để quản lý filter state
  const {
    filters,
    initialFilters,
    handleFilterChange: hookHandleFilterChange,
    handlePriceRangeChange: hookHandlePriceRangeChange,
    handlePriceInputChange: hookHandlePriceInputChange,
    handleReset,
    applyFilters,
  } = useFilterState({
    aggregations,
    onLoadingChange,
    onFilterApplied,
  })

  // Local state cho mobile (chỉ apply khi click "Áp dụng")
  const [mobileFilters, setMobileFilters] = useState<FilterState>(filters)

  // Sync mobileFilters khi filters thay đổi (từ URL)
  useEffect(() => {
    setMobileFilters(filters)
  }, [filters])

  // Check if current view is mobile
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 900

  // Handle filter change - delegate cho mobile hoặc hook
  const handleFilterChange = (attributeCode: string, value: string) => {
    if (isMobile()) {
      // Mobile: update local state
      setMobileFilters((prev) => {
        const currentValues = prev[attributeCode] as string[]
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value]
        return { ...prev, [attributeCode]: newValues }
      })
    } else {
      // Desktop: use hook (auto debounce apply)
      hookHandleFilterChange(attributeCode, value)
    }
  }

  // Handle price range change
  const handlePriceRangeChange = (attributeCode: string, newValue: number | number[]) => {
    if (isMobile()) {
      setMobileFilters((prev) => ({
        ...prev,
        [attributeCode]: newValue as [number, number],
      }))
    } else {
      hookHandlePriceRangeChange(attributeCode, newValue)
    }
  }

  // Handle price input change
  const handlePriceInputChange = (attributeCode: string, type: 'min' | 'max', value: string) => {
    const numValue = Number(value.replace(/\D/g, ''))

    if (isMobile()) {
      setMobileFilters((prev) => {
        const currentRange = prev[attributeCode] as [number, number]
        return {
          ...prev,
          [attributeCode]: type === 'min' ? [numValue, currentRange[1]] : [currentRange[0], numValue],
        }
      })
    } else {
      hookHandlePriceInputChange(attributeCode, type, value)
    }
  }

  // Mobile: Apply filters khi click button
  const handleApplyClickMobile = () => {
    applyFilters(mobileFilters)
    onClose()
  }

  // Mobile: Reset cũng reset local state
  const handleResetClick = () => {
    if (isMobile()) {
      setMobileFilters(initialFilters)
    }
    handleReset()
  }

  // Get current filters based on view type
  const currentFilters = isMobile() ? mobileFilters : filters

  if (isLoading) {
    return (
      <Stack height="100%" pr={{ xs: 2, sm: 3 }} spacing={2} width={'100%'}>
        <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
          <Skeleton width={'100%'} height={32} />
          <Skeleton variant="circular" width={24} height={24} />
        </Stack>

        <Stack direction="row" spacing={1}>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} width={'100%'} height={28} />
          ))}
        </Stack>
        <Divider />
        <Box flex={1}>
          {[...Array(3)].map((_, i) => (
            <Stack key={i} spacing={1} mb={2}>
              <Skeleton width="40%" height={24} />
              {[...Array(3)].map((_, j) => (
                <Skeleton key={j} height={32} />
              ))}
              {i < 2 && <Divider sx={{ my: 1 }} />}
            </Stack>
          ))}
        </Box>
      </Stack>
    )
  }
  // Render filter section based on aggregation type
  const renderFilterSection = (agg: IAggregations, open: boolean) => {
    const filterType = determineFilterType(agg.attribute_code, agg.label)
    const selectedValues = currentFilters[agg.attribute_code] || []

    // Bỏ qua nếu không có options (trừ price)
    if (!agg.options || agg.options.length === 0) {
      return null
    }

    switch (filterType) {
      case 'checkbox':
        return (
          <CheckboxFilter
            key={agg.attribute_code}
            attributeCode={agg.attribute_code}
            label={agg.label}
            defaultOpen={open}
            options={agg.options}
            selectedValues={selectedValues as string[]}
            onChange={handleFilterChange}
          />
        )

      case 'size':
        return (
          <SizeFilter
            key={agg.attribute_code}
            attributeCode={agg.attribute_code}
            label={agg.label}
            defaultOpen={open}
            options={agg.options}
            selectedValues={selectedValues as string[]}
            onChange={handleFilterChange}
          />
        )

      case 'color':
        return (
          <ColorFilter
            key={agg.attribute_code}
            attributeCode={agg.attribute_code}
            label={agg.label}
            defaultOpen={open}
            options={agg.options}
            selectedValues={selectedValues as string[]}
            onChange={handleFilterChange}
          />
        )

      case 'price':
        return (
          <PriceFilter
            key={agg.attribute_code}
            attributeCode={agg.attribute_code}
            label={agg.label}
            options={agg.options}
            priceRange={selectedValues as [number, number]}
            onChange={handlePriceRangeChange}
            onInputChange={handlePriceInputChange}
          />
        )

      default:
        return null
    }
  }
  const filterContent = (
    <Stack height={'100%'} spacing={2}>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          display: { xs: 'flex', xl: 'none' },
          color: 'error.main',
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
          border: '1px solid #ccc',
          borderRadius: '0',
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Stack spacing={2} pr={{ xs: 2 }}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems="center">
          <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 600 }}>Bộ lọc</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleResetClick} sx={{ color: 'primary.main' }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                display: { xs: 'none', md: 'block' },
              }}
              onClick={handleResetClick}
            >
              Làm mới
            </Typography>
          </Box>
        </Stack>
        <Divider />
      </Stack>

      {/* <Stack spacing={2} pr={{ xs: 2, xl: 0 }}>
        <CurrentFilterTag listCurrentTagsFilter={listCurrentTagsFilter} handleRemoveTag={handleRemoveTag} />
 
      </Stack> */}

      <Box
        flex={1}
        sx={{
          pr: { xs: 2 },
          maxHeight: { xs: 'calc(100vh - var(--size-hd))' },
          overflowY: { xs: 'auto' },
          overflowX: 'hidden',
        }}
      >
        {aggregations.map((agg, index) => {
          const isDefaultOpen = index < 2 // Mở 2 filter đầu tiên theo mặc định
          return (
            <Fragment key={agg.attribute_code}>
              {renderFilterSection(agg, isDefaultOpen)}
              {index < aggregations.length - 1 && renderFilterSection(agg, isDefaultOpen) && (
                <Divider sx={{ my: 1, borderColor: 'grey.200' }} />
              )}
            </Fragment>
          )
        })}
      </Box>

      <Stack spacing={2} pt={2} pr={{ xs: 2, xl: 0 }} sx={{ display: { xs: 'block', xl: 'none' } }}>
        <Divider />
        <Button fullWidth onClick={handleApplyClickMobile} variant="contained" size="large">
          Áp dụng
        </Button>
      </Stack>
    </Stack>
  )

  return (
    <>
      {/* DESKTOP VERSION */}
      <Box
        sx={{
          display: { xs: 'none', xl: 'block' },
          width: '100%',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        {filterContent}
      </Box>
      {/* MOBILE VERSION*/}
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', xl: 'none' },
          '& .MuiDrawer-paper': {
            width: '350px',
            maxWidth: '100%',
            p: 2,
            pr: 0,
          },
        }}
      >
        {filterContent}
      </Drawer>
    </>
  )
}
